import { BaseElementDef, BaseElementDefBase, ILockableElement, LockAction } from "./BaseElementDef";
import { first, map, switchMap } from "rxjs/operators";
import { combineLatest, from, Observable } from "rxjs";
import { DfParseFloatWithoutNan } from "../../lib/calculations";
const DEFAULT_PAYMENT_SUBMIT_TEXT = "Continue to Payment";
import { FormItem } from "@cs/FormItem";
import { FormDefinitionItem } from "@/public/pages/elements/__DefinitionDataInterface";
import { AbstractPaySettings } from "@cs-ts/abstract-pay-settings";
import { PaymentItem } from "@cs-ts/payment-item";
import { CalculationContext } from "./__CalculationContextInterface";
//import type FormDef from "./FormDef";

export default class AbstractPayDef<T extends AbstractPaySettings> extends BaseElementDef<T, FormDefinitionItem<T>, FormItem> implements ILockableElement {
    constructor(parentElement: BaseElementDefBase | null, elementData: FormItem, definitionData: FormDefinitionItem<T>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFAbstractPay"; // TODO - not hardcoded?
    }

    shouldSkipConfirm() {
        return this.shouldSkipConfirm$().pipe(first()).toPromise();
    }

    shouldSkipConfirm$() {
        return this.getCalculatedItems$().pipe(
            map(items => {
                return items.reduce((a, c) => a + c.quantity * c.unitPrice, 0) > 0 ? true : null;
            }),
        );
    }

    hasCustomSubmitText$() {
        return this.submitText$().pipe(map(i => i != DEFAULT_PAYMENT_SUBMIT_TEXT));
    }

    getLockActions(lock: string): LockAction[] {
        return [];
    }

    submitText$() {
        return this.shouldSkipConfirm$().pipe(
            switchMap(skip => {
                if (!skip) {
                    return from([null]);
                }
                const observable = this.resolveCalculation(this.definition.settings.paymentSubmitButtonTextCalculationId!);
                return observable.pipe(map(value => (value && value.length ? value.join("") : this.definition.settings.paymentSubmitButtonText) || DEFAULT_PAYMENT_SUBMIT_TEXT));
            }),
        );
    }

    getCalculatedItems() {
        return this.getCalculatedItems$().pipe(first()).toPromise();
    }

    getCalculatedItems$() {
        //const formStatus = (this.getRoot() as FormDef).status;
        // TODO status observable
        //const triggeringStatuses = [this.definition.settings.preStatus, this.definition.settings.pendingStatus].filter(i => i != null && i != "");
        //if (triggeringStatuses.length > 0 && !triggeringStatuses.includes(formStatus)) {
        //    return from([[]]);
        //}
        type JsItem = { quantity: number; unitPrice: number; description: string; sku: string };
        const items = [] as (readonly [JsItem[], Observable<(readonly [keyof JsItem, null | string[]])[]>])[];
        for (let itemIndex = 0; itemIndex < (this.definition.settings.items?.length ?? 0); itemIndex++) {
            const itemConstants = this.definition.settings.items![itemIndex];
            const itemItems: JsItem[] = [
                {
                    quantity: itemConstants.quantity == null ? 1 : itemConstants.quantity,
                    unitPrice: itemConstants.unitPrice || 0,
                    description: itemConstants.description || "",
                    sku: itemConstants.sku || "",
                },
            ];
            items.push([itemItems, combineLatest((Object.keys(itemItems[0]) as (keyof JsItem)[]).map(i => [i, itemConstants[(i + "CalculationId") as keyof PaymentItem]] as const).map(([k, c]) => (c == null ? from([[k, null] as const]) : this.resolveCalculation(c as string).pipe(map(rV => [k, rV] as const)))))] as const);
        }
        return combineLatest(items.map(([itemItems, calcSubject]) => calcSubject.pipe(map(resolvedCalc => [itemItems, resolvedCalc] as const)))).pipe(
            map(inputItems => {
                const items = [] as JsItem[];
                for (const [itemItems, calculations] of inputItems) {
                    if (calculations.some(i => i[1] != null && i[1].length == 0)) {
                        continue;
                    }
                    //eslint-disable-next-line prefer-const
                    for (let [key, values] of calculations) {
                        if (values == null) {
                            continue;
                        }
                        if (key.endsWith("Calculation")) {
                            key = key.slice(0, -1 * "Calculation".length) as keyof JsItem;
                        }
                        for (let valueIndex = 0; valueIndex < values.length; valueIndex++) {
                            if (valueIndex >= itemItems.length) {
                                itemItems.push({} as JsItem);
                            }
                            if (key == "unitPrice" || key == "quantity") {
                                itemItems[valueIndex][key] = DfParseFloatWithoutNan(values[valueIndex]);
                            } else {
                                itemItems[valueIndex][key] = values[valueIndex];
                            }
                        }
                    }
                    for (const key of Object.keys(itemItems[0]) as (keyof JsItem)[]) {
                        for (const item of itemItems) {
                            if (item[key] == null) {
                                if (key == "unitPrice" || key == "quantity") {
                                    item[key] = DfParseFloatWithoutNan(itemItems[0][key]);
                                } else {
                                    item[key] = itemItems[0][key];
                                }
                            }
                        }
                    }
                    Array.prototype.push.apply(items, itemItems);
                }
                return items;
            }),
        );
    }
}
